import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Card';
import { SealBadge } from '../../components/ui/SealBadge';
import { api } from '../../lib/api';
import type { ConsultationBooking } from '@bharat-consultancy/shared';

export default function ConsultThankYou() {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('bookingId');
  const [booking, setBooking] = useState<ConsultationBooking | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!bookingId) return;

    // Same pattern as Booking/ThankYou.tsx: the Razorpay webhook (server-
    // verified) is the source of truth for marking the booking scheduled,
    // and it can land a moment after this redirect fires. Short poll so
    // the page can show "Scheduled" without the visitor refreshing.
    let attempts = 0;
    const maxAttempts = 6;
    const poll = () => {
      api.consultations
        .get(bookingId)
        .then((res) => {
          setBooking(res.booking);
          if (res.booking.status === 'pending_payment' && attempts < maxAttempts) {
            attempts += 1;
            setTimeout(poll, 1500);
          } else {
            setIsLoading(false);
          }
        })
        .catch(() => setIsLoading(false));
    };
    poll();
  }, [bookingId]);

  return (
    <>
      <SEOHead title="Consultation Confirmed — Bharat Consultancy" description="" canonicalPath="/consult/thank-you" />

      <section className="flex min-h-[70vh] items-center justify-center bg-paper px-4 py-12">
        <div className="w-full max-w-lg text-center">
          <div className="mx-auto w-fit">
            <SealBadge size="lg">
              <span className="block text-lg">✓</span>
              Call
              <br />
              Booked
            </SealBadge>
          </div>

          <h1 className="mt-8 font-display text-3xl font-bold text-ink">Your Consultation is Confirmed</h1>

          {isLoading ? (
            <div className="mt-6 flex justify-center">
              <Spinner className="h-6 w-6 text-seal-blue" />
            </div>
          ) : (
            <>
              <p className="mt-3 text-ink/70">
                {booking?.status === 'scheduled'
                  ? 'Your payment was successful. Our team will call you as scheduled.'
                  : "We're confirming your payment — this page will update shortly."}
              </p>
              {booking?.preferredDate && (
                <p className="mt-2 text-ink/70">
                  We'll call you on <strong>{booking.preferredDate}</strong>
                  {booking.preferredTime && (
                    <>
                      {' '}
                      at <strong>{booking.preferredTime}</strong>
                    </>
                  )}
                  .
                </p>
              )}
            </>
          )}

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="tel:+918170896296">
              <Button variant="outline">Call Us</Button>
            </a>
            <a href="https://wa.me/918170896296" target="_blank" rel="noreferrer">
              <Button variant="outline">WhatsApp Us</Button>
            </a>
            <Link to="/services">
              <Button>Browse Services</Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
